import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Components
import SiteNavbar from "./components/SiteNavbar";

// Main Pages
import LandingPage from "./pages/LandingPage";
import BirthdayMeaningPaintingsPage from "./pages/BirthdayMeaningPaintingsPage";
import MusicVisualizer from "./pages/MusicVisualizer";
import FaceJokesPage from "./pages/FaceJokesPage";

const YOUTUBE_URL = "https://www.youtube.com/@tantalizingart";

/**
 * Scrolls to the top when the user changes page.
 * This makes the site feel cleaner, especially with a fixed navbar.
 */
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [location.pathname]);

  return null;
}

/**
 * Sends users to the official Tantalizing.Art YouTube page.
 * Daily affirmations are hosted only there.
 */
function YouTubeRedirect() {
  useEffect(() => {
    window.location.replace(YOUTUBE_URL);
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 text-center bg-black text-white">
      <div className="max-w-xl">
        <h1 className="text-3xl md:text-4xl font-black mb-4">
          Opening Daily Affirmations
        </h1>
        <p className="text-zinc-400 mb-8 leading-relaxed">
          Daily affirmations are hosted on the official Tantalizing.Art YouTube
          page.
        </p>
        <a
          href={YOUTUBE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-[#00B4A6] hover:bg-[#00796B] text-white font-black transition-all duration-300"
        >
          Open YouTube Page
        </a>
      </div>
    </div>
  );
}

/**
 * Simple 404 page.
 * Keeps visitors inside the website instead of showing a blank screen.
 */
function NotFoundPage() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 text-center bg-black text-white">
      <div className="max-w-xl">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00B4A6]/10 border border-[#00B4A6]/25 mb-6">
          <span className="text-3xl">✨</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-black mb-4">
          This page wandered off.
        </h1>

        <p className="text-zinc-400 mb-8 leading-relaxed">
          The page you are looking for does not exist, or its route has changed.
          The good news: the art is still alive.
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a
            href="/"
            className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-white text-black hover:bg-zinc-200 font-black transition-all duration-300"
          >
            Go Home
          </a>

          <a
            href="/birthday-meaning-paintings"
            className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-[#00B4A6] hover:bg-[#00796B] text-white font-black transition-all duration-300"
          >
            Birthday Paintings
          </a>
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />

      <Routes>
        {/* Main website routes */}
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/birthday-meaning-paintings"
          element={<BirthdayMeaningPaintingsPage />}
        />
        <Route path="/music-visualizer" element={<MusicVisualizer />} />
        <Route path="/mirror-of-cheer" element={<FaceJokesPage />} />

        {/* Daily affirmations live only on YouTube */}
        <Route path="/daily-affirmations" element={<YouTubeRedirect />} />
        <Route path="/affirmations" element={<YouTubeRedirect />} />
        <Route path="/youtube" element={<YouTubeRedirect />} />

        {/* Legacy / old route redirects */}
        <Route
          path="/birthdaymeaningpaintings"
          element={<Navigate to="/birthday-meaning-paintings" replace />}
        />
        <Route
          path="/birthday-meaning"
          element={<Navigate to="/birthday-meaning-paintings" replace />}
        />
        <Route
          path="/birthday-paintings"
          element={<Navigate to="/birthday-meaning-paintings" replace />}
        />
        <Route
          path="/musicvisualizer"
          element={<Navigate to="/music-visualizer" replace />}
        />
        <Route
          path="/face-jokes"
          element={<Navigate to="/mirror-of-cheer" replace />}
        />
        <Route
          path="/mirrorofcheer"
          element={<Navigate to="/mirror-of-cheer" replace />}
        />

        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-black text-white font-sans selection:bg-[#00B4A6] selection:text-white">
        <SiteNavbar />

        {/* Padding prevents pages from hiding underneath the fixed navbar */}
        <main className="pt-[78px] md:pt-[82px]">
          <AppRoutes />
        </main>
      </div>
    </Router>
  );
}

export default App;
